const IMPORT_SCHEMA = "coetic-x.invoice-studio.invoice-import";
const PARSER_VERSION = "2026.09.26-1";
const MAX_PAGES = 50;
const MAX_TOKENS = 1200;
const MAX_TOKEN_LENGTH = 240;

const text = (value) => [...String(value ?? "")]
  .map((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || (code >= 127 && code <= 159) ? " " : character;
  })
  .join("")
  .replace(/\s+/g, " ")
  .trim();
const normalized = (value) => text(value)
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase();

const DATE_MONTHS = Object.freeze({
  jan: 1, january: 1, janar: 1,
  feb: 2, february: 2, shkurt: 2,
  mar: 3, march: 3, mars: 3,
  apr: 4, april: 4, prill: 4,
  may: 5, maj: 5,
  jun: 6, june: 6, qershor: 6,
  jul: 7, july: 7, korrik: 7,
  aug: 8, august: 8, gusht: 8,
  sep: 9, sept: 9, september: 9, shtator: 9,
  oct: 10, october: 10, tetor: 10,
  nov: 11, november: 11, nentor: 11,
  dec: 12, december: 12, dhjetor: 12,
});

export const INVOICE_IMPORT_SCHEMA = IMPORT_SCHEMA;
export const invoiceImportParserVersion = PARSER_VERSION;
// Optional-field recognition changed; the retained PDF coordinate format did not.
export const invoiceImportCoordinateVersions = Object.freeze(['2026.09.02-1', PARSER_VERSION]);

export const INVOICE_IMPORT_FIELDS = Object.freeze([
  { key: "supplier", label: "Supplier or merchant", type: "text", required: true, placeholder: "Business shown on the document" },
  { key: "supplierTaxNumber", label: "Supplier fiscal or business ID", type: "identifier", required: false, placeholder: "Fiscal or business ID" },
  { key: "supplierVatNumber", label: "Supplier VAT number", type: "identifier", required: false, placeholder: "VAT registration number" },
  { key: "supplierCountry", label: "Supplier country", type: "text", required: false, placeholder: "Country shown on the document" },
  { key: "reference", label: "Invoice or receipt number", type: "text", required: false, placeholder: "Document reference" },
  { key: "issuedDate", label: "Issue date", type: "date", required: true, placeholder: "YYYY-MM-DD" },
  { key: "dueDate", label: "Due date", type: "date", required: false, placeholder: "Optional for receipts" },
  { key: "currency", label: "Currency", type: "currency", required: true, placeholder: "EUR" },
  { key: "netAmount", label: "Net amount", type: "amount", required: false, placeholder: "0.00" },
  { key: "taxAmount", label: "Tax amount", type: "amount", required: false, placeholder: "0.00" },
  { key: "grossAmount", label: "Total amount", type: "amount", required: true, placeholder: "0.00" },
]);

const FIELD_BY_KEY = new Map(INVOICE_IMPORT_FIELDS.map((field) => [field.key, field]));

const FIELD_RULES = Object.freeze({
  supplier: {
    label: /^(?:supplier|vendor|merchant|furnitor(?:i)?|shites(?:i)?|from)\b/,
    rawLabel: /^(?:supplier|vendor|merchant|furnitor(?:i)?|shit[eë]s(?:i)?|from)\b\s*(?:name)?\s*[:#-]?\s*/i,
    reject: /^(?:supplier|vendor|merchant)\s+(?:vat|tax|fiscal|country|address|phone|registration)\b/,
  },
  supplierTaxNumber: {
    label: /\b(?:tax (?:number|no|id)|fiscal (?:number|no)|numri fiskal|numri unik|numri i biznesit|nipt|business id|registration number)\b/,
    rawLabel: /\b(?:(?:supplier|seller|buyer|customer|vendor)\s+)?(?:tax\s*(?:number|no\.?|id)|fiscal\s*(?:number|no\.?|id)|numri\s+(?:fiskal|unik)|numri\s+i\s+biznesit|nipt|business\s+id|registration\s+number)\b\s*[:#-]?\s*/i,
    reject: /\b(?:vat|tvsh)\b/,
  },
  supplierVatNumber: {
    label: /\b(?:vat (?:number|no|id)|vat registration|numri (?:i )?tvsh|nr\.? ?tvsh|tvsh number)\b/,
    rawLabel: /\b(?:(?:supplier|seller|buyer|customer|vendor)\s+)?(?:vat\s*(?:number|no\.?|id|registration)|numri\s+(?:i\s+)?tvsh(?:-s[eë])?|nr\.?\s*tvsh|tvsh\s*(?:number|no\.?|id))\b\s*[:#-]?\s*/i,
  },
  supplierCountry: {
    label: /\b(?:country|shteti|vend(?:i)?)\b/,
    rawLabel: /\b(?:country|shteti|vend(?:i)?)\b\s*[:#-]?\s*/i,
  },
  reference: {
    label: /\b(?:invoice (?:number|num|no|nr)|document (?:number|num|no|nr)|receipt (?:number|num|no|nr)|fatura? (?:number|num|no|nr)|numri i fatures|reference|referenca)\b/,
    rawLabel: /^(?:(?:invoice|document|receipt|fatur[aë]?)(?=\s|[:#]|$)|numri\s+i\s+fatur[eë]s\b|reference\b|referenca\b)\s*(?:number|num\.?|no\.?|nr\.?|#)?\s*[:#-]?\s*/i,
  },
  issuedDate: {
    label: /\b(?:issue date|issued date|date of issue|invoice date|date issued|receipt date|document date|data e leshimit|data e fatures|data)\b/,
    rawLabel: /\b(?:issue\s+date|issued\s+date|date\s+of\s+issue|invoice\s+date|date\s+issued|receipt\s+date|document\s+date|data\s+e\s+l[eë]shimit|data\s+e\s+fatur[eë]s|data)\b\s*[:#-]?\s*/i,
    reject: /\b(?:due|afati|payment)\b/,
  },
  dueDate: {
    label: /\b(?:due date|date due|due on|payment due|pay by|afati i pageses|data e pageses|date d echeance|zahlbar bis|scadenza)\b/,
    rawLabel: /\b(?:due\s+date|date\s+due|due\s+on|payment\s+due|pay\s+by|afati\s+i\s+pages[eë]s|data\s+e\s+pages[eë]s|date\s+d.?echeance|zahlbar\s+bis|scadenza)\b\s*[:#-]?\s*/i,
  },
  netAmount: {
    label: /\b(?:subtotal|sub total|net total|net amount|amount before tax|nentotal(?:i)?|pa tvsh)\b/,
    rawLabel: /\b(?:subtotal|sub\s+total|net\s+total|net\s+amount|amount\s+before\s+tax|n[eë]ntotal(?:i)?|pa\s+tvsh)\b\s*[:#-]?\s*/i,
  },
  taxAmount: {
    label: /\b(?:vat amount|vat total|tax amount|tax total|tax|tvsh|tatimi)\b/,
    rawLabel: /\b(?:vat\s+amount|vat\s+total|tax\s+amount|tax\s+total|tax|tvsh|tatimi)\b(?:\s*\d+(?:[.,]\d+)?\s*%)?\s*[:#-]?\s*/i,
    reject: /\b(?:number|numri|nr|no|id|rate|registration)\b/,
  },
  grossAmount: {
    label: /\b(?:grand total|amount paid|paid amount|amount due|balance due|total due|total to pay|shuma totale|per pagese|totali|total)\b/,
    rawLabel: /\b(?:grand\s+total|amount\s+paid|paid\s+amount|amount\s+due|balance\s+due|total\s+due|total\s+to\s+pay|shuma\s+totale|p[eë]r\s+pages[eë]|totali|total)\b\s*[:#-]?\s*/i,
    reject: /\b(?:subtotal|net total|vat total|tax total)\b/,
  },
});

const validIsoDate = (year, month, day) => {
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (date.getUTCFullYear() !== Number(year) || date.getUTCMonth() !== Number(month) - 1 || date.getUTCDate() !== Number(day)) return "";
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const normalizeInvoiceDate = (value) => {
  const candidate = normalized(value)
    .replace(/(\d)\s+(?=\d)/g, "$1")
    .replace(/\s*([./-])\s*/g, "$1");
  if (!candidate) return "";
  const iso = candidate.match(/\b(20\d{2})[-/.](\d{1,2})[-/.](\d{1,2})\b/);
  if (iso) return validIsoDate(iso[1], iso[2], iso[3]);
  const dayFirst = candidate.match(/\b(\d{1,2})[-/.](\d{1,2})[-/.](20\d{2})\b/);
  if (dayFirst) return validIsoDate(dayFirst[3], dayFirst[2], dayFirst[1]);
  const namedMonth = candidate.match(/\b(\d{1,2})\s+([a-z]+)\s+(20\d{2})\b/);
  if (namedMonth && DATE_MONTHS[namedMonth[2]]) return validIsoDate(namedMonth[3], DATE_MONTHS[namedMonth[2]], namedMonth[1]);
  const monthFirst = candidate.match(/\b([a-z]+)\s+(\d{1,2}),?\s+(20\d{2})\b/);
  if (monthFirst && DATE_MONTHS[monthFirst[1]]) return validIsoDate(monthFirst[3], DATE_MONTHS[monthFirst[1]], monthFirst[2]);
  return "";
};

const normalizeReference = (value) => text(value)
  .replace(/(\d)\s+(?=\d)/g, "$1")
  .replace(/\s*([/#-])\s*/g, "$1")
  .slice(0, 160);

const normalizeIdentifier = (value) => {
  const candidate = text(value);
  if (/^[A-Za-z0-9 ./-]+$/.test(candidate) && /\d/.test(candidate)) return candidate.replace(/\s+/g, "").slice(0, 160);
  return candidate.slice(0, 160);
};

const normalizeNumber = (value) => {
  let candidate = text(value).replace(/[\s'’]/g, "").replace(/[^0-9,.-]/g, "");
  if (!candidate || !/\d/.test(candidate)) return "";
  const comma = candidate.lastIndexOf(",");
  const dot = candidate.lastIndexOf(".");
  if (comma >= 0 && dot >= 0) {
    const decimal = comma > dot ? "," : ".";
    const thousands = decimal === "," ? /\./g : /,/g;
    candidate = candidate.replace(thousands, "").replace(decimal, ".");
  } else if (comma >= 0) {
    const digitsAfter = candidate.length - comma - 1;
    candidate = digitsAfter > 0 && digitsAfter <= 4 ? candidate.replace(/,/g, ".") : candidate.replace(/,/g, "");
  } else if (dot >= 0) {
    const dots = (candidate.match(/\./g) || []).length;
    const digitsAfter = candidate.length - dot - 1;
    if (dots > 1 || digitsAfter > 4 || digitsAfter === 3) candidate = candidate.replace(/\./g, "");
  }
  const number = Number(candidate);
  if (!Number.isFinite(number)) return "";
  return number.toFixed(2);
};

export const normalizeInvoiceAmount = (value) => {
  const matches = text(value).match(/-?\d[\d\s'’.,]*/g) || [];
  for (const match of matches) {
    const normalizedValue = normalizeNumber(match);
    if (normalizedValue) return normalizedValue;
  }
  return "";
};

export const normalizeInvoiceCurrency = (value) => {
  const candidate = text(value).toUpperCase();
  const code = candidate.match(/\b(EUR|USD|GBP|CHF|ALL|CAD|AUD|SEK|NOK|DKK|PLN|CZK|RSD|MKD)\b/)?.[1];
  if (code) return code;
  if (candidate.includes("€")) return "EUR";
  if (candidate.includes("£")) return "GBP";
  if (candidate.includes("$")) return "USD";
  return "";
};

const textAfterLabel = (value, rule) => {
  const raw = text(value);
  if (!raw) return "";
  if (rule?.rawLabel) {
    const without = raw.replace(rule.rawLabel, "").replace(/^\s*[:#-]\s*/, "").trim();
    if (without !== raw && without) return without;
  }
  const separated = raw.match(/[:#]\s*(.+)$/)?.[1];
  return text(separated || "");
};

const PARTY_LABEL_ONLY = /^(?:from|to|bill(?:ed)? to|invoice to|ship to|sold to|supplier|vendor|merchant|seller|buyer|customer|furnitor(?:i)?|shit[eë]s(?:i)?|bler[eë]s(?:i)?)\s*[:#-]?$/i;

export const invoiceImportValueFromText = (fieldKey, value) => {
  const field = FIELD_BY_KEY.get(fieldKey);
  if (!field) return "";
  if (field.type === "date") return normalizeInvoiceDate(value);
  if (field.type === "amount") return normalizeInvoiceAmount(value);
  if (field.type === "currency") return normalizeInvoiceCurrency(value);
  const rule = FIELD_RULES[fieldKey];
  const tail = textAfterLabel(value, rule);
  const candidate = text(tail || value);
  if (fieldKey === "supplier" && PARTY_LABEL_ONLY.test(candidate)) return "";
  if (fieldKey === "reference") return normalizeReference(candidate);
  if (field.type === "identifier") return normalizeIdentifier(candidate);
  return candidate.slice(0, fieldKey === "supplier" ? 200 : 160);
};

const segmentText = (tokens = []) => tokens.reduce((result, token, index) => {
  if (!index) return text(token.text);
  const previous = tokens[index - 1];
  const gap = Number(token.x || 0) - (Number(previous.x || 0) + Number(previous.width || 0));
  const joinsSplitWord = gap <= 1.5 && /^[A-Z]$/.test(text(previous.text)) && /^[a-z]{3,}$/.test(text(token.text));
  return `${result}${joinsSplitWord ? "" : " "}${text(token.text)}`;
}, "");

const lineSegments = (tokens = []) => {
  const segments = [];
  for (const token of tokens) {
    const previous = segments[segments.length - 1];
    const previousToken = previous?.tokens[previous.tokens.length - 1];
    const gap = previousToken ? Number(token.x || 0) - (Number(previousToken.x || 0) + Number(previousToken.width || 0)) : 0;
    const splitGap = Math.max(34, Math.max(Number(token.height || 0), Number(previousToken?.height || 0)) * 3.2);
    if (!previous || gap > splitGap) segments.push({ tokens: [token] });
    else previous.tokens.push(token);
  }
  return segments.map((segment) => ({
    ...segment,
    x: Number(segment.tokens[0]?.x || 0),
    text: text(segmentText(segment.tokens)),
  }));
};

const lineGroups = (tokens = []) => {
  const groups = new Map();
  tokens.forEach((token) => {
    const key = `${token.page}:${token.line}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(token);
  });
  return [...groups.values()]
    .map((lineTokens) => {
      const sorted = [...lineTokens].sort((left, right) => Number(left.x || 0) - Number(right.x || 0));
      return {
        page: sorted[0]?.page || 1,
        line: sorted[0]?.line || 1,
        tokens: sorted,
        segments: lineSegments(sorted),
        text: text(sorted.map((token) => token.text).join(" ")),
      };
    })
    .sort((left, right) => left.page - right.page || left.line - right.line);
};

const assignment = (fieldKey, value, confidence, method, token, line) => ({
  fieldKey,
  value,
  confidence: Number(confidence.toFixed(2)),
  method,
  tokenIds: token ? [token.id] : (line?.tokens || []).map((item) => item.id),
  sourceLocation: token
    ? { page: token.page, line: token.line, x: token.x, y: token.y, token_ids: [token.id] }
    : line ? { page: line.page, line: line.line, token_ids: line.tokens.map((item) => item.id) } : { kind: method },
  sourceText: text(token?.text || line?.text).slice(0, MAX_TOKEN_LENGTH),
});

const firstUsableToken = (fieldKey, line, startIndex) => {
  for (let index = Math.max(0, startIndex); index < line.tokens.length; index += 1) {
    const token = line.tokens[index];
    const value = invoiceImportValueFromText(fieldKey, token.text);
    if (value) return { token, value, distance: index - startIndex };
  }
  return null;
};

const findLabeledField = (fieldKey, lines) => {
  const rule = FIELD_RULES[fieldKey];
  if (!rule) return null;
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex];
    const matchingSegments = (line.segments || []).filter((segment) => {
      const candidate = normalized(segment.text);
      return rule.label.test(candidate) && !(rule.reject && rule.reject.test(candidate));
    });
    if (!matchingSegments.length) {
      const candidate = normalized(line.text);
      if (rule.label.test(candidate) && !(rule.reject && rule.reject.test(candidate))) matchingSegments.push(line);
    }
    if (!matchingSegments.length) continue;
    const segment = matchingSegments[0];
    const directTail = textAfterLabel(segment.text, rule);
    const directValue = directTail ? invoiceImportValueFromText(fieldKey, directTail) : "";
    if (directValue) return assignment(fieldKey, directValue, 0.95, "label-same-segment", null, { ...line, tokens: segment.tokens, text: segment.text });
    const segmentIndex = (line.segments || []).indexOf(segment);
    const rightSegments = segmentIndex >= 0 ? line.segments.slice(segmentIndex + 1) : [];
    for (const right of rightSegments) {
      const rightValue = invoiceImportValueFromText(fieldKey, right.text);
      if (rightValue) return assignment(fieldKey, rightValue, 0.92, "label-same-line", null, { ...line, tokens: right.tokens, text: right.text });
    }
    const labelTokenIndex = line.tokens.findIndex((token) => rule.label.test(normalized(token.text)));
    const current = firstUsableToken(fieldKey, line, labelTokenIndex >= 0 ? labelTokenIndex + 1 : 0);
    if (current && !rule.label.test(normalized(current.token.text))) return assignment(fieldKey, current.value, current.distance > 1 ? 0.84 : 0.9, "label-same-line-token", current.token, line);
    const nextLine = lines[lineIndex + 1];
    if (nextLine?.page === line.page) {
      const nextSegments = [...(nextLine.segments || [nextLine])];
      if (fieldKey === "supplier") {
        nextSegments.sort((left, right) => Math.abs(Number(left.x || 0) - Number(segment.x || 0)) - Math.abs(Number(right.x || 0) - Number(segment.x || 0)));
      }
      for (const nextSegment of nextSegments) {
        const nextValue = invoiceImportValueFromText(fieldKey, nextSegment.text);
        if (nextValue && !(rule.label.test(normalized(nextSegment.text)))) {
          const horizontalDistance = Math.abs(Number(nextSegment.x || 0) - Number(segment.x || 0));
          const confidence = fieldKey === "supplier" && horizontalDistance <= 80 ? 0.9 : 0.79;
          return assignment(fieldKey, nextValue, confidence, fieldKey === "supplier" ? "label-next-line-region" : "label-next-line", null, { ...nextLine, tokens: nextSegment.tokens, text: nextSegment.text });
        }
      }
    }
  }
  return null;
};

const supplierFallback = (tokens, lines) => {
  const firstPageLines = lines.filter((line) => line.page === 1);
  const candidates = firstPageLines.flatMap((line, lineIndex) => (line.segments || []).map((segment) => {
    const nextLine = firstPageLines.find((candidateLine) => candidateLine.line === line.line + 1);
    const suffix = (nextLine?.segments || []).find((nextSegment) => Math.abs(Number(nextSegment.x || 0) - Number(segment.x || 0)) < 60 && /^(?:l\.?l\.?c\.?|ltd\.?|limited|gmbh|sh\.?p\.?k\.?)$/i.test(text(nextSegment.text)));
    return {
      ...segment,
      page: line.page,
      line: line.line,
      lineIndex,
      text: text([segment.text, suffix?.text].filter(Boolean).join(" ")),
      tokens: [...segment.tokens, ...(suffix?.tokens || [])],
    };
  }))
    .filter((candidate) => text(candidate.text).length >= 3
      && text(candidate.text).length <= 120
      && /[A-Za-z\u00C0-\u024F]/.test(candidate.text)
      && !PARTY_LABEL_ONLY.test(text(candidate.text))
      && !/^(?:l\.?l\.?c\.?|ltd\.?|limited|gmbh|sh\.?p\.?k\.?)$/i.test(text(candidate.text))
      && !/\b(?:invoice|receipt|fature|fatura|total|date|data|address|tel|email|www|bank|amount|bill to|ship to|customer)\b/i.test(normalized(candidate.text))
      && !/^[A-Z]{3}\s*[\d.,-]+$/i.test(text(candidate.text)));
  if (!candidates.length) return null;
  candidates.sort((left, right) => {
    const score = (candidate) => (/\b(?:l\.?l\.?c|ltd|limited|gmbh|sh\.?p\.?k|inc|company|group|associates)\b/i.test(candidate.text) ? 12 : 0)
      + (candidate.lineIndex < 8 ? 5 : 0)
      + (candidate.x < 220 ? 3 : 0)
      + Math.min(3, Math.max(...candidate.tokens.map((token) => Number(token.height || 0))) / 7);
    return score(right) - score(left) || left.lineIndex - right.lineIndex;
  });
  const candidate = candidates[0];
  const value = text(candidate.text);
  const evidenceTokens = candidate.tokens;
  const companySuffix = /\b(?:l\.?l\.?c|ltd|limited|gmbh|sh\.?p\.?k|inc|company|group|associates)\b/i.test(value);
  return assignment("supplier", value, companySuffix ? 0.8 : 0.58, "top-page-heading", null, { page: 1, line: candidate.line, tokens: evidenceTokens, text: value });
};

const billToAnchor = (lines) => {
  for (const line of lines) {
    const segment = (line.segments || []).find((candidate) => /\b(?:bill(?:ed)? to|invoice to|buyer|customer|bler[eë]si|fatura p[eë]r)\b/.test(normalized(candidate.text)));
    if (segment) return { page: line.page, line: line.line, x: segment.x, segment };
  }
  return null;
};

const buyerFallback = (lines, anchor) => {
  if (!anchor) return null;
  const tail = textAfterLabel(anchor.segment.text, { rawLabel: /\b(?:bill(?:ed)?\s+to|invoice\s+to|buyer|customer|bler[eë]si|fatura\s+p[eë]r)\b\s*[:#-]?\s*/i });
  if (tail) return assignment("supplier", tail, 0.96, "buyer-label-same-segment", null, { page: anchor.page, line: anchor.line, tokens: anchor.segment.tokens, text: anchor.segment.text });
  for (const line of lines) {
    if (line.page !== anchor.page || line.line <= anchor.line || line.line > anchor.line + 3) continue;
    const candidate = (line.segments || []).find((segment) => Math.abs(Number(segment.x || 0) - Number(anchor.x || 0)) < 70
      && /[A-Za-z\u00C0-\u024F]/.test(segment.text)
      && !/\b(?:address|adresa|tax|fiscal|numri|vat|tvsh|phone|mob|email)\b/.test(normalized(segment.text)));
    if (candidate) return assignment("supplier", candidate.text, 0.9, "buyer-label-next-line", null, { page: line.page, line: line.line, tokens: candidate.tokens, text: candidate.text });
  }
  return null;
};

const currencyFallback = (tokens, grossAssignment) => {
  const preferred = grossAssignment?.sourceLocation?.line
    ? tokens.filter((token) => token.page === grossAssignment.sourceLocation.page && token.line === grossAssignment.sourceLocation.line)
    : [];
  const candidate = [...preferred, ...tokens].find((token) => normalizeInvoiceCurrency(token.text));
  return candidate ? assignment("currency", normalizeInvoiceCurrency(candidate.text), 0.9, "explicit-currency", candidate) : null;
};

// Keep optional party details inside that party's address block, not the other
// company's tax details elsewhere on the page.
const partyAddressLines = (lines, party) => {
  if (!party?.tokenIds?.length) return [];
  const anchor = lines.flatMap(line => line.tokens).find(token => party.tokenIds.includes(token.id));
  if (!anchor) return [];
  let stopped = false;
  return lines.filter(line => line.page === anchor.page && line.line >= anchor.line && line.line <= anchor.line + 12).map(line => {
    const segments = line.segments.filter(segment => Math.abs(segment.x - anchor.x) <= 90);
    if (line.line > anchor.line && segments.some(segment => /^(?:bill(?:ed)? to|invoice to|ship to|buyer|customer|supplier|vendor|from|to)(?:\s*[:#-]|\s*$)/i.test(segment.text))) stopped = true;
    return { ...line, segments: stopped ? [] : segments, tokens: stopped ? [] : segments.flatMap(segment => segment.tokens), text: stopped ? '' : segments.map(segment => segment.text).join(' ') };
  }).filter(line => line.tokens.length);
};

let countryNames;
const countryFromAddress = lines => {
  if (!countryNames) {
    countryNames = new Map([['kosovo','Kosovo'],['kosova','Kosovo'],['usa','United States'],['uk','United Kingdom']]);
    if (typeof Intl.DisplayNames === 'function') {
      const english = new Intl.DisplayNames(['en'], { type:'region' });
      const albanian = new Intl.DisplayNames(['sq'], { type:'region' });
      for (let a = 65; a <= 90; a++) for (let b = 65; b <= 90; b++) {
        const code = String.fromCharCode(a,b), name = english.of(code);
        if (name !== code) { countryNames.set(normalized(name), name); countryNames.set(normalized(albanian.of(code)), name); }
      }
    }
  }
  for (const line of lines) for (const segment of line.segments) {
    const name = countryNames.get(normalized(segment.text));
    if (name) return assignment('supplierCountry', name, 0.8, 'party-address-country', null, {...line, tokens:segment.tokens, text:segment.text});
  }
  return null;
};

const dueDateFromTerms = (lines, issued) => {
  if (!issued?.value) return null;
  for (const line of lines) {
    const days = normalized(line.text).match(/\b(?:net\s+(\d{1,3})(?:\s+days)?|(?:payment due|payable)\s+(?:within|in)\s+(\d{1,3})\s+days)\b/);
    const count = Number(days?.[1] || days?.[2]);
    if (!count || count > 365) continue;
    const due = new Date(`${issued.value}T00:00:00Z`); due.setUTCDate(due.getUTCDate() + count);
    return {...assignment('dueDate', due.toISOString().slice(0,10), 0.78, 'explicit-payment-terms', null, line), tokenIds:[...issued.tokenIds, ...line.tokens.map(token => token.id)]};
  }
  return null;
};

export const inferInvoiceDocumentType = (tokens = [], fallback = "unknown_document") => {
  const firstPage = normalized(tokens.filter((token) => token.page === 1).slice(0, 160).map((token) => token.text).join(" "));
  if (/\b(?:receipt|cash receipt|kupon|fature e vogel)\b/.test(firstPage)) return "receipt";
  if (/\b(?:invoice|fature|fatura|facture|rechnung|fattura)\b/.test(firstPage)) {
    return fallback === "sales_invoice" ? "sales_invoice" : "purchase_invoice";
  }
  return fallback;
};

export const requiredInvoiceImportFields = (documentType) => {
  const keys = ["supplier", "issuedDate", "currency", "grossAmount"];
  if (documentType !== "receipt") keys.splice(1, 0, "reference");
  return keys;
};

export const mapInvoiceImportFields = (tokens = [], { documentType = "purchase_invoice" } = {}) => {
  const lines = lineGroups(tokens);
  const fields = {};
  ["reference", "issuedDate", "dueDate", "netAmount", "taxAmount", "grossAmount"].forEach((fieldKey) => {
    const match = findLabeledField(fieldKey, lines);
    if (match) fields[fieldKey] = match;
  });
  if (documentType === "sales_invoice") {
    const anchor = billToAnchor(lines);
    fields.supplier = buyerFallback(lines, anchor);
    const partyLines = partyAddressLines(lines, fields.supplier);
    ["supplierTaxNumber", "supplierVatNumber", "supplierCountry"].forEach((fieldKey) => {
      const match = findLabeledField(fieldKey, partyLines);
      if (match) fields[fieldKey] = match;
    });
  } else {
    fields.supplier = findLabeledField('supplier', lines) || supplierFallback(tokens, lines);
    const partyLines = partyAddressLines(lines, fields.supplier);
    ["supplierTaxNumber", "supplierVatNumber", "supplierCountry"].forEach((fieldKey) => {
      const match = findLabeledField(fieldKey, partyLines);
      if (match) fields[fieldKey] = match;
    });
  }
  if (!fields.supplierCountry) fields.supplierCountry = countryFromAddress(partyAddressLines(lines, fields.supplier));
  if (!fields.dueDate) fields.dueDate = dueDateFromTerms(lines, fields.issuedDate);
  fields.currency = currencyFallback(tokens, fields.grossAmount);
  Object.keys(fields).forEach((key) => { if (!fields[key]?.value) delete fields[key]; });
  const required = requiredInvoiceImportFields(documentType);
  return {
    fields,
    unresolved: required.filter((key) => !fields[key]?.value),
    lowConfidence: Object.values(fields).filter((field) => field.confidence < 0.82).map((field) => field.fieldKey),
  };
};

const pdfRuntime = async (provided) => {
  if (provided) return provided.default || provided;
  const module = await import("pdfjs-dist/legacy/build/pdf.mjs");
  return module.default || module;
};

const byDescendingY = (left, right) => right.y - left.y;
const byAscendingX = (left, right) => left.x - right.x;

const pdfTextRows = (items = []) => {
  const rows = [];
  for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
    const item = items[itemIndex];
    const value = text(item.str).slice(0, MAX_TOKEN_LENGTH);
    if (!value) continue;
    const x = Number(item.transform?.[4] || 0);
    const y = Number(item.transform?.[5] || 0);
    let row = null;
    for (const candidate of rows) {
      if (Math.abs(candidate.y - y) <= 2.5) {
        row = candidate;
        break;
      }
    }
    if (!row) {
      row = { y, items: [] };
      rows.push(row);
    }
    row.items.push({ value, x, y, width: Number(item.width || 0), height: Number(item.height || Math.abs(item.transform?.[3] || 0)), itemIndex });
  }
  return rows.sort(byDescendingY);
};

export const extractPdfTextTokens = async (file, {
  pdfModule = null,
  pdfWorkerSrc = "/pdf.worker.min.js",
  pdfStandardFontDataUrl = "",
} = {}) => {
  const pdfjs = await pdfRuntime(pdfModule);
  if (pdfWorkerSrc && pdfjs.GlobalWorkerOptions) pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
  const data = new Uint8Array(await file.arrayBuffer());
  const document = await pdfjs.getDocument({
    data,
    ...(pdfStandardFontDataUrl ? { standardFontDataUrl: pdfStandardFontDataUrl } : {}),
  }).promise;
  const tokens = [];
  const pageDimensions = {};
  const pageCount = Math.min(document.numPages, MAX_PAGES);
  let truncated = document.numPages > MAX_PAGES;
  try {
    for (let pageNumber = 1; pageNumber <= pageCount && tokens.length < MAX_TOKENS; pageNumber += 1) {
      const page = await document.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1 });
      pageDimensions[pageNumber] = { width: Number(viewport.width.toFixed(2)), height: Number(viewport.height.toFixed(2)) };
      const content = await page.getTextContent();
      const orderedRows = pdfTextRows(content.items);
      for (let lineIndex = 0; lineIndex < orderedRows.length; lineIndex += 1) {
        const orderedItems = orderedRows[lineIndex].items.sort(byAscendingX);
        for (const item of orderedItems) {
          if (tokens.length >= MAX_TOKENS) {
            truncated = true;
            break;
          }
          tokens.push({
            id: `p${pageNumber}-t${item.itemIndex}`,
            text: item.value,
            page: pageNumber,
            line: lineIndex + 1,
            x: Number(item.x.toFixed(2)),
            y: Number(item.y.toFixed(2)),
            width: Number(item.width.toFixed(2)),
            height: Number(item.height.toFixed(2)),
          });
        }
      }
    }
  } finally {
    if (typeof document.destroy === "function") await document.destroy();
  }
  return { tokens, pageCount: document.numPages, pageDimensions, truncated };
};

export const extractInvoiceDocument = async (file, options = {}) => {
  const extracted = await extractPdfTextTokens(file, options);
  const documentType = inferInvoiceDocumentType(extracted.tokens, options.documentType || "unknown_document");
  const mapped = mapInvoiceImportFields(extracted.tokens, { documentType });
  const requiresOcr = extracted.tokens.length === 0;
  const warnings = [
    requiresOcr ? "No selectable PDF text was found. Use manual mapping now or an OCR adapter when available." : null,
    extracted.truncated ? `Text extraction was capped at ${MAX_PAGES} pages and ${MAX_TOKENS} text items.` : null,
    mapped.unresolved.length ? `Review ${mapped.unresolved.length} required field${mapped.unresolved.length === 1 ? "" : "s"}.` : null,
    mapped.lowConfidence.length ? `Confirm ${mapped.lowConfidence.length} low-confidence suggestion${mapped.lowConfidence.length === 1 ? "" : "s"}.` : null,
  ].filter(Boolean);
  return {
    schema: IMPORT_SCHEMA,
    parserVersion: PARSER_VERSION,
    documentType,
    pageCount: extracted.pageCount,
    pageDimensions: extracted.pageDimensions,
    tokenCount: extracted.tokens.length,
    textLayer: extracted.tokens.length > 0,
    requiresOcr,
    truncated: extracted.truncated,
    fields: mapped.fields,
    unresolved: mapped.unresolved,
    lowConfidence: mapped.lowConfidence,
    tokens: extracted.tokens,
    warnings,
  };
};

const evidenceAlias = Object.freeze({
  supplier: "supplier",
  reference: "reference",
  issuedDate: "documentDate",
  dueDate: "dueDate",
  currency: "currency",
  grossAmount: "grossAmount",
  supplierTaxNumber: "supplierTaxNumber",
  supplierVatNumber: "supplierVatNumber",
  supplierCountry: "supplierCountry",
  netAmount: "netAmount",
  taxAmount: "taxAmount",
});

export const seedInvoiceImportReview = ({ extraction = {}, reviewFields = {}, accountingEvidence = {} } = {}) => {
  const saved = reviewFields?.invoiceImport || {};
  const values = {};
  const assignments = {};
  const suggestions = extraction.tokens?.length ? mapInvoiceImportFields(extraction.tokens, {documentType:saved.documentType || extraction.documentType}).fields : {};
  INVOICE_IMPORT_FIELDS.forEach((field) => {
    const extractedField = extraction?.fields?.[field.key] || (!field.required ? suggestions[field.key] : null);
    const evidenceValue = accountingEvidence?.[evidenceAlias[field.key]];
    const value = saved.values?.[field.key] ?? evidenceValue ?? extractedField?.value ?? "";
    values[field.key] = text(value);
    assignments[field.key] = saved.assignments?.[field.key] || (extractedField ? {
      confidence: extractedField.confidence,
      method: extractedField.method,
      tokenIds: extractedField.tokenIds || [],
      sourceLocation: extractedField.sourceLocation || {},
      sourceText: extractedField.sourceText || "",
    } : null);
  });
  return { values, assignments };
};

export const invoiceImportAccountingEvidence = (values = {}, existing = {}) => ({
  ...existing,
  supplier: text(values.supplier),
  supplierTaxNumber: text(values.supplierTaxNumber),
  supplierVatNumber: text(values.supplierVatNumber),
  supplierCountry: text(values.supplierCountry),
  reference: text(values.reference),
  documentDate: text(values.issuedDate),
  dueDate: text(values.dueDate),
  currency: normalizeInvoiceCurrency(values.currency) || text(values.currency).toUpperCase(),
  // Reviewed values already use the invoice decimal grammar. Do not run the
  // permissive source-text parser here: it guesses separators and rounds cents.
  // Keep invalid text too, so validation cannot mistake it for an optional blank.
  netAmount: text(values.netAmount),
  taxAmount: text(values.taxAmount),
  grossAmount: text(values.grossAmount),
  sourceLocation: "Reviewed through PDF field mapping",
});

export const createInvoiceImportReviewPayload = ({ values = {}, assignments = {}, extraction = {}, documentType = "purchase_invoice", exportProfile = "generic", ediPosting = {}, savedAt = new Date().toISOString() } = {}) => ({
  schema: IMPORT_SCHEMA,
  version: 1,
  documentType,
  parserVersion: extraction.parserVersion || PARSER_VERSION,
  values: Object.fromEntries(INVOICE_IMPORT_FIELDS.map((field) => [field.key, text(values[field.key])])),
  assignments: Object.fromEntries(INVOICE_IMPORT_FIELDS.map((field) => [field.key, assignments[field.key] || { confidence: null, method: "manual-entry", tokenIds: [], sourceLocation: { kind: "manual-entry" } }])),
  exportProfile,
  ediPosting: exportProfile === "generic" ? {} : {
    profile: exportProfile,
    treatmentId: text(ediPosting.treatmentId),
    identifier: text(ediPosting.identifier),
    vatNumber: text(ediPosting.vatNumber),
    amountEur: text(ediPosting.amountEur),
    exchangeRate: text(ediPosting.exchangeRate),
    rowNumber: text(ediPosting.rowNumber || "1"),
    allocations: Array.isArray(ediPosting.allocations) ? ediPosting.allocations.map((allocation, index) => ({
      id: text(allocation?.id) || `allocation-${index + 1}`,
      treatmentId: text(allocation?.treatmentId),
      amountEur: text(allocation?.amountEur),
    })) : [],
  },
  savedAt,
});

export const invoiceImportEvidence = (invoice = {}) => Object.values(invoice.fields || {}).map((field) => ({
  field_key: `invoice.${field.fieldKey}`,
  value: field.value,
  source_location: field.sourceLocation || {},
  confidence: field.confidence,
}));
